import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement, createUniqueId } from '../lib-element';
import '../icon/lib-icon';
import '../button/lib-button';
import { LibMultiSelectOption } from './lib-multiselect-option';
import css from './lib-multiselect.css';

@customElement('lib-multiselect')
export class LibMultiSelect extends LibElement {
  protected override get hostFocusSelector(): string { return '.lib-multiselect-field'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @state() private items: { value: string; text: string }[] = [];
  @property({ type: Boolean, attribute: 'show-select-all' }) showSelectAll = false;
  @property({ attribute: 'select-all-text' }) selectAllText = 'Select All';
  @property() value = '';
  private revision = 0;
  @property() headline = '';
  @property() placeholder = '';
  @property({ attribute: 'selected-text' }) selectedText = 'Selected:';
  @property({ attribute: 'search-placeholder' }) searchPlaceholder = 'Search Filter...';
  @property({ attribute: 'apply-button-text' }) applyButtonText = 'Apply';
  @property({ type: Boolean, attribute: 'hide-search' }) hideSearch = false;
  @property({ type: Boolean, attribute: 'hide-apply' }) hideApply = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) error = false;
  @property() message = '';
  @property() surface = 'white';
  @property({ attribute: 'aria-label' }) override ariaLabel: string | null = null;
  @state() private open = false;
  @state() private active = -1;
  @state() private up = false;
  @state() private maxHeight = 624;
  @state() private query = '';
  private readonly idPrefix = createUniqueId('lib-multiselect');
  private readonly optionsObserver = new MutationObserver(() => this.syncOptions());

  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'server-state']; }

  override attributeChangedCallback(name: string, previous: string | null, value: string | null): void {
    if (name === 'server-state') {
      if (value !== null) {
        const state = JSON.parse(value) as { value: string; revision: number };
        if (state.revision >= this.revision) this.value = state.value;
      }
      return;
    }
    if (name === 'value' && this.hasAttribute('server-state')) return;
    super.attributeChangedCallback(name, previous, value);
  }

  private get selection(): string[] { return this.value ? [...new Set(this.value.split(','))] : []; }
  private get filtered() {
    const query = this.query.trim().toLocaleLowerCase();
    return this.items.filter(item => item.text.toLocaleLowerCase().includes(query));
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.optionsObserver.observe(this, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value'] });
    this.syncOptions();
  }

  private syncOptions(): void {
    const items = Array.from(this.children)
      .filter((child): child is LibMultiSelectOption => child instanceof LibMultiSelectOption)
      .map(option => ({ value: option.value, text: option.textContent ?? '' }));
    if (items.length === this.items.length && items.every((item, index) =>
      item.value === this.items[index].value && item.text === this.items[index].text)) return;
    this.items = items;
  }

  override disconnectedCallback(): void {
    this.optionsObserver.disconnect();
    document.removeEventListener('pointerdown', this.outside, true);
    super.disconnectedCallback();
  }

  override updated(): void {
    if (this.open && (this.disabled || !this.items.length)) this.close();
    if (this.active >= this.filtered.length) this.active = this.filtered.length - 1;
  }

  private readonly outside = (event: PointerEvent) => {
    if (!event.composedPath().includes(this)) this.close();
  };

  private close(focus = false): void {
    this.open = false;
    this.active = -1;
    this.query = '';
    document.removeEventListener('pointerdown', this.outside, true);
    if (focus) this.renderRoot.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  }

  private async show(last = false): Promise<void> {
    if (this.disabled || !this.items.length || this.open) return;
    const rect = this.renderRoot.querySelector('button')!.getBoundingClientRect();
    const { top, bottom } = this.getVisibleBounds();
    const below = Math.max(0, bottom - rect.bottom), above = Math.max(0, rect.top - top);
    const needed = Math.min(624, this.items.length * 48 + (this.showSelectAll ? 48 : 0) + 24 + (this.hideSearch ? 0 : 56) + (this.hideApply ? 0 : 56));
    this.up = below < needed && (above >= needed || above > below);
    this.maxHeight = Math.max(120, Math.min(624, (this.up ? above : below) - 8));
    this.query = '';
    const selected = this.items.findIndex(item => this.selection.includes(item.value));
    const fallback = last ? this.items.length - 1 : 0;
    const initialOption = selected >= 0 ? selected : fallback;
    this.active = this.showSelectAll ? -1 : initialOption;
    this.open = true;
    document.addEventListener('pointerdown', this.outside, true);
    await this.updateComplete;
    const optionSelector = this.showSelectAll ? '.lib-multiselect-select-all input' : 'ul';
    const focusSelector = this.hideSearch ? optionSelector : '.lib-multiselect-search-input';
    this.renderRoot.querySelector<HTMLElement>(focusSelector)?.focus({ preventScroll: true });
    this.scrollActive();
  }

  private getVisibleBounds(): { top: number; bottom: number } {
    let top = 0, bottom = innerHeight;
    for (let node = this.parentElement; node; node = node.parentElement) {
      if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(node).overflowY)) {
        const bounds = node.getBoundingClientRect();
        top = Math.max(top, bounds.top + node.clientTop);
        bottom = Math.min(bottom, bounds.top + node.clientTop + node.clientHeight);
      }
    }
    return { top, bottom };
  }

  private scrollActive(): void {
    const list = this.renderRoot.querySelector('ul');
    const option = list?.children[this.active] as HTMLElement | undefined;
    if (!list || !option) return;
    const offset = option.getBoundingClientRect().top - list.getBoundingClientRect().top;
    if (offset < 0) list.scrollTop += offset;
    else if (offset + option.offsetHeight > list.clientHeight) list.scrollTop += offset + option.offsetHeight - list.clientHeight;
  }

  private toggle(index: number): void {
    if (this.disabled || index < 0 || index >= this.filtered.length) return;
    const value = this.filtered[index].value, selection = this.selection;
    this.value = (selection.includes(value) ? selection.filter(item => item !== value) : [...selection, value]).join(',');
    this.notifySelection();
  }

  private notifySelection(): void {
    this.revision++;
    this.dispatchEvent(new CustomEvent('lib-multiselect-change', {
      detail: { value: this.value, revision: this.revision }, bubbles: true, composed: true,
    }));
    if (!this.hasAttribute('server-state'))
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    if (!this.hideSearch) this.renderRoot.querySelector<HTMLInputElement>('.lib-multiselect-search-input')?.focus({ preventScroll: true });
  }

  private async search(event: InputEvent): Promise<void> {
    this.query = (event.target as HTMLInputElement).value;
    this.active = Math.min(this.active, this.filtered.length - 1);
    await this.updateComplete;
    this.scrollActive();
  }

  private async key(event: KeyboardEvent): Promise<void> {
    if (this.disabled) return;
    const key = event.key, search = event.target instanceof HTMLInputElement;
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ', 'Spacebar'].includes(key) && !(search && [' ', 'Spacebar'].includes(key))) event.preventDefault();
    if (key === 'Escape') { this.close(true); return; }
    if (key === 'Tab' && this.showSelectAll && search && !event.shiftKey && this.filtered.length) {
      event.preventDefault();
      this.renderRoot.querySelector<HTMLInputElement>('.lib-multiselect-select-all input')?.focus();
      return;
    }
    if (key === 'Tab') {
      if (this.open) this.renderRoot.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
      this.close(); return;
    }
    if (!this.open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Spacebar'].includes(key)) await this.show(key === 'ArrowUp');
      return;
    }
    if (key === 'Enter' || (!search && [' ', 'Spacebar'].includes(key))) { this.toggle(this.active); return; }
    if (this.shouldFocusSelectAll(key)) {
      this.active = -1;
      await this.updateComplete;
      this.renderRoot.querySelector<HTMLInputElement>('.lib-multiselect-select-all input')?.focus({ preventScroll: true });
      return;
    }
    await this.moveActive(key);
  }

  private shouldFocusSelectAll(key: string): boolean {
    return this.showSelectAll && this.filtered.length > 0 &&
      ((key === 'ArrowUp' && this.active <= 0) || (key === 'ArrowDown' && this.active < 0) || key === 'Home');
  }

  private async moveActive(key: string): Promise<void> {
    const count = this.filtered.length;
    if (key === 'ArrowDown') this.active = Math.min(count - 1, this.active + 1);
    if (key === 'ArrowUp') this.active = count ? Math.max(0, this.active - 1) : -1;
    if (key === 'Home') this.active = count ? 0 : -1;
    if (key === 'End') this.active = count - 1;
    await this.updateComplete;
    this.scrollActive();
  }

  private get controlClasses(): string {
    return ['lib-multiselect', this.open ? 'lib-multiselect-open' : '', this.up && this.open ? 'lib-multiselect-up' : '',
      this.disabled ? 'lib-multiselect-disabled' : '', this.error ? 'lib-multiselect-error' : '', this.surface === 'grey' ? 'lib-multiselect-grey' : ''].join(' ');
  }

  override render() {
    const id = this.idPrefix, selection = this.selection;
    const text = selection.map(value => this.items.find(item => item.value === value)?.text ?? '').join(', ');
    const selectionPrefix = this.selectedText ? this.selectedText + ' ' : '';
    const displayText = selection.length ? selectionPrefix + text : this.placeholder;
    const messageId = `${id}-message`;
    const activeId = this.open && this.active >= 0 ? `${id}-option-${this.active}` : nothing;
    const labelId = `${id}-label`;
    const listboxId = `${id}-listbox`;
    const valueClass = `lib-multiselect-value${selection.length ? '' : ' lib-multiselect-placeholder'}`;
    const label = this.headline ? html`<label class="lib-multiselect-label" for=${id} id=${labelId}><span class="lib-multiselect-label-text">${this.headline}</span><slot name="help"></slot></label>` : nothing;
    const badge = selection.length ? html`<span class="lib-multiselect-badge" aria-hidden="true">${selection.length}</span>` : nothing;
    const message = this.message ? html`<div id=${messageId} class="lib-multiselect-message">${this.message}</div>` : nothing;
    return html`<slot hidden @slotchange=${this.syncOptions}></slot><div class=${this.controlClasses}>
      ${label}
      <div class="lib-multiselect-anchor">
        <button type="button" id=${id} class="lib-multiselect-field" role="combobox" aria-haspopup="listbox"
          aria-expanded=${String(this.open)} aria-controls=${listboxId} aria-activedescendant=${activeId}
          aria-label=${this.ariaLabel || nothing} aria-labelledby=${this.headline ? labelId : nothing}
          aria-invalid=${this.error ? 'true' : nothing} aria-describedby=${this.message ? messageId : nothing}
          ?disabled=${this.disabled} @keydown=${this.key} @click=${() => this.open ? this.close() : this.show()}>
          <span class=${valueClass}>${displayText}</span>
          ${badge}
          <span class="lib-multiselect-chevron" aria-hidden="true"><lib-icon icon-url="_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/btn-chevron-down.svg"></lib-icon></span>
        </button>
        ${this.open ? this.renderFlyout(id, activeId, selection) : nothing}
      </div>
      ${message}
    </div>`;
  }

  private renderFlyout(id: string, activeId: string | typeof nothing, selection: string[]) {
    const maxHeightStyle = `max-height: ${this.maxHeight}px`;
    const listboxId = `${id}-listbox`;
    const listLabel = this.headline ? `${id}-label` : id;
    const options = this.filtered.map((item, index) => {
      const optionId = `${id}-option-${index}`;
      const classes = `lib-multiselect-option${index === this.active ? ' lib-multiselect-option-active' : ''}`;
      const checkboxClass = `lib-multiselect-checkbox${selection.includes(item.value) ? ' lib-multiselect-checkbox-checked' : ''}`;
      return html`<li id=${optionId} role="option" class=${classes}
        aria-selected=${String(selection.includes(item.value))} @click=${() => this.toggle(index)}>
        <span class=${checkboxClass} aria-hidden="true"></span>
        <span class="lib-multiselect-option-text">${item.text}</span></li>`;
    });
    const search = !this.hideSearch ? html`<div class="lib-multiselect-search"><input type="text" class="lib-multiselect-search-input"
            placeholder=${this.searchPlaceholder} .value=${this.query} aria-label=${this.searchPlaceholder}
            aria-controls=${listboxId} aria-activedescendant=${activeId} @input=${this.search} @keydown=${this.key}>
            <span class="lib-multiselect-search-icon" aria-hidden="true"><lib-icon icon-url="_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/search.svg"></lib-icon></span></div>` : nothing;
    const matches = this.filtered;
    const selectedCount = matches.filter(item => selection.includes(item.value)).length;
    const partialSelectionClass = selectedCount > 0 ? ' lib-multiselect-checkbox-indeterminate' : '';
    const selectAllCheckboxClass = selectedCount > 0 && selectedCount === matches.length
      ? ' lib-multiselect-checkbox-checked' : partialSelectionClass;
    const selectAll = this.showSelectAll ? html`<label class="lib-multiselect-option lib-multiselect-select-all">
      <input type="checkbox" .checked=${matches.length > 0 && selectedCount === matches.length}
        .indeterminate=${selectedCount > 0 && selectedCount < matches.length}
        ?disabled=${this.disabled || !matches.length}
        @keydown=${(event: KeyboardEvent) => {
          if (this.disabled) return;
          if (event.key === 'Tab') {
            if (event.shiftKey && !this.hideSearch) {
              event.preventDefault();
              this.renderRoot.querySelector<HTMLInputElement>('.lib-multiselect-search-input')?.focus({ preventScroll: true });
            } else {
              this.close(true);
            }
            return;
          }
          if (event.key === 'Enter') { event.preventDefault(); (event.target as HTMLInputElement).click(); }
          if (event.key === 'ArrowUp' && !this.hideSearch) {
            event.preventDefault();
            this.active = -1;
            this.renderRoot.querySelector<HTMLInputElement>('.lib-multiselect-search-input')?.focus({ preventScroll: true });
          }
          if (event.key === 'Escape') { event.preventDefault(); this.close(true); }
          if (event.key === 'ArrowDown' || event.key === 'End') {
            event.preventDefault(); this.active = event.key === 'End' ? matches.length - 1 : 0;
            this.renderRoot.querySelector<HTMLElement>('ul')?.focus({ preventScroll: true });
            void this.updateComplete.then(() => this.scrollActive());
          }
        }}
        @change=${() => {
          if (this.disabled || !matches.length) return;
          const values = new Set(matches.map(item => item.value));
          this.value = (selectedCount === matches.length
            ? selection.filter(value => !values.has(value))
            : [...new Set([...selection, ...values])]).join(',');
          this.notifySelection();
        }}><span class=${`lib-multiselect-checkbox${selectAllCheckboxClass}`} aria-hidden="true"></span>
      <span class="lib-multiselect-option-text">${this.selectAllText}</span></label>` : nothing;
    const apply = !this.hideApply ? html`<div class="lib-multiselect-apply"><lib-button full-width @click=${() => this.close(true)}><lib-icon icon-url="_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/tick-sign.svg"></lib-icon>${this.applyButtonText}</lib-button></div>` : nothing;
    return html`<div class="lib-multiselect-flyout" style=${maxHeightStyle}>
          ${search}
          ${selectAll}
          <ul id=${listboxId} class="lib-multiselect-listbox" role="listbox" aria-activedescendant=${activeId} tabindex="-1" aria-multiselectable="true"
            aria-labelledby=${listLabel} @keydown=${this.key}>
            ${options}
          </ul>
          ${apply}
        </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-multiselect': LibMultiSelect; }
}
